/**
 * api/client — the single fetch wrapper the SPA uses to reach the dashboard JSON API
 * (src/web/api.ts).
 *
 * - Base URL: `VITE_API_BASE` at build time (the real dashboard origin), else
 *   same-origin `/api/*` — which dev/preview answers with the demo mock and prod
 *   answers with the co-located backend.
 * - Auth: the dashboard token (mirrors the fleet bearer/session pattern) is attached
 *   as `Authorization: Bearer <token>` from the session store on every call.
 * - A 401 clears the session and rejects with {@link UnauthorizedError} so the shell
 *   can bounce to the login screen.
 */
import { clearToken, getToken } from '../auth/session';
import type {
  AnalyticsSummaryResponse,
  DecisionsAnalyticsResponse,
  DecisionsResponse,
  EquityCurveResponse,
  EquityRange,
  KillOutcome,
  OverviewResponse,
  PositionReviewsResponse,
  PositionsResponse,
  ReArmResponse,
  SettingsResponse,
  SettingsSaveResponse,
  StateResponse,
  StatsResponse,
  TradesResponse,
  TunableSettings,
} from './types';

const API_BASE = (import.meta.env.VITE_API_BASE ?? '').replace(/\/$/, '');

/**
 * The kill switch requires this confirm token in its POST body (mirrors
 * `KILL_CONFIRM_TOKEN` server-side): auth alone is not enough to flatten the book —
 * an accidental or replayed request without the token is rejected 400.
 */
export const KILL_CONFIRM_TOKEN = 'HALT';

/** The confirm token the re-arm endpoint requires (mirrors `REARM_CONFIRM_TOKEN`). */
export const REARM_CONFIRM_TOKEN = 'REARM';

export class ApiError extends Error {
  constructor(readonly status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}
export class UnauthorizedError extends ApiError {
  constructor(message = 'unauthorized') {
    super(401, message);
    this.name = 'UnauthorizedError';
  }
}

interface RequestOptions {
  readonly method?: string;
  readonly body?: unknown;
  readonly signal?: AbortSignal;
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = { accept: 'application/json' };
  if (token) headers.authorization = `Bearer ${token}`;
  if (opts.body !== undefined) headers['content-type'] = 'application/json';

  const init: RequestInit = { method: opts.method ?? 'GET', headers };
  if (opts.body !== undefined) init.body = JSON.stringify(opts.body);
  if (opts.signal) init.signal = opts.signal;

  const res = await fetch(`${API_BASE}${path}`, init);

  if (res.status === 401) {
    clearToken();
    throw new UnauthorizedError();
  }
  if (!res.ok) {
    const detail = await safeErrorMessage(res);
    throw new ApiError(res.status, detail);
  }
  return (await res.json()) as T;
}

async function safeErrorMessage(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { error?: string };
    if (body && typeof body.error === 'string') return body.error;
  } catch {
    /* non-JSON body */
  }
  return `request failed (${res.status})`;
}

/**
 * The SSE stream URL. EventSource cannot set an `Authorization` header, so the bearer
 * rides as a `?token=` query param — the demo backend honours it, and same-origin prod
 * falls back to the session cookie / poll transport when it does not (see useLive).
 */
export function streamUrl(): string {
  const token = getToken();
  const qs = token ? `?token=${encodeURIComponent(token)}` : '';
  return `${API_BASE}/api/stream${qs}`;
}

export const api = {
  /** Verify the current token by hitting an authed endpoint; used at login. */
  async state(signal?: AbortSignal): Promise<StateResponse> {
    return request<StateResponse>('/api/state', signal ? { signal } : {});
  },
  async overview(signal?: AbortSignal): Promise<OverviewResponse> {
    return request<OverviewResponse>('/api/overview', signal ? { signal } : {});
  },
  async positions(signal?: AbortSignal): Promise<PositionsResponse> {
    return request<PositionsResponse>('/api/positions', signal ? { signal } : {});
  },
  /**
   * The EX.8 open-position committee reviews (HOLD / TIGHTEN_STOP / EXIT_NOW + reasoning +
   * the live-lifecycle snapshot each was taken against). Newest-first; optionally scoped
   * to one symbol. Powers the position-management view's stop trail and decision timeline.
   */
  async positionReviews(signal?: AbortSignal, symbol?: string): Promise<PositionReviewsResponse> {
    const qs = symbol ? `?symbol=${encodeURIComponent(symbol)}` : '';
    return request<PositionReviewsResponse>(`/api/position-reviews${qs}`, signal ? { signal } : {});
  },
  async equityCurve(range: EquityRange, signal?: AbortSignal): Promise<EquityCurveResponse> {
    return request<EquityCurveResponse>(`/api/equity-curve?range=${range}`, signal ? { signal } : {});
  },
  async decisions(signal?: AbortSignal, limit = 30): Promise<DecisionsResponse> {
    return request<DecisionsResponse>(`/api/decisions?limit=${limit}`, signal ? { signal } : {});
  },
  async trades(signal?: AbortSignal, limit = 100): Promise<TradesResponse> {
    return request<TradesResponse>(`/api/trades?limit=${limit}`, signal ? { signal } : {});
  },
  async stats(signal?: AbortSignal): Promise<StatsResponse> {
    return request<StatsResponse>('/api/stats', signal ? { signal } : {});
  },
  /**
   * The server-computed decision funnel (TA.1b): candidates → committee → ENTER, the
   * NO-TRADE reason tally, committee confidence histograms and per-role agreement.
   * Powers the Funnel view so the operator sees WHY the desk is (not) trading.
   */
  async analyticsDecisions(signal?: AbortSignal): Promise<DecisionsAnalyticsResponse> {
    return request<DecisionsAnalyticsResponse>('/api/analytics/decisions', signal ? { signal } : {});
  },
  /** Headline KPI summary (TA.1b); the Funnel view reads `totalTrades` for its executed tail. */
  async analyticsSummary(signal?: AbortSignal): Promise<AnalyticsSummaryResponse> {
    return request<AnalyticsSummaryResponse>('/api/analytics/summary', signal ? { signal } : {});
  },
  /**
   * The AI-analysis export bundle (TA.1): one JSON blob of the full analytics surface.
   * The endpoint may not exist on every backend yet — callers that want a guaranteed
   * download catch a 404/ApiError and fall back to a client-assembled bundle.
   */
  async export(signal?: AbortSignal): Promise<unknown> {
    return request<unknown>('/api/export', signal ? { signal } : {});
  },
  /** The tunable settings + the read-only owner risk limits (TF.6). */
  async settings(signal?: AbortSignal): Promise<SettingsResponse> {
    return request<SettingsResponse>('/api/settings', signal ? { signal } : {});
  },
  /**
   * Persist a tunable-settings change (TF.6). PUT — the server REJECTS any risk-shaped
   * field 400 (risk limits are owner-only) and, on success, re-reads the file and echoes
   * exactly what landed on disk (the round-trip contract).
   */
  async saveSettings(next: TunableSettings): Promise<SettingsSaveResponse> {
    return request<SettingsSaveResponse>('/api/settings', { method: 'PUT', body: next });
  },
  /** The one privileged, confirm-guarded action. Runs dry in demo. */
  async kill(reason: string): Promise<KillOutcome> {
    return request<KillOutcome>('/api/killswitch', {
      method: 'POST',
      body: { confirm: KILL_CONFIRM_TOKEN, reason },
    });
  },
  /**
   * The inverse of {@link kill}: clear the durable halt so the book can trade again
   * (owner re-arm). Confirm-guarded server-side with its own token; returns the
   * post-clear halt state.
   */
  async reArm(reason: string): Promise<ReArmResponse> {
    return request<ReArmResponse>('/api/rearm', {
      method: 'POST',
      body: { confirm: REARM_CONFIRM_TOKEN, reason },
    });
  },
};
